'use client';

// The Voice control in the composer's end actions, beside and distinct from
// the dictation mic (BUILD 2 C2-8, WP2-8 step 4).
//
// - Shown when the deployment offers voice mode and the person's voice config
//   has loaded; disabled, with the reason as its description, when voice mode
//   cannot start (the company switched it off, no engine, the microphone held
//   by a meeting, an insecure page or a browser that cannot capture). No path
//   records and then fails for a reason known in advance.
// - The first use shows the disclosure: what is sent where, auto-send and
//   undo, that approvals always need the person, and how to leave. "Turn on"
//   is the gesture that starts voice mode (and saves `enabled`); "Not now"
//   closes it.
// - The `#/voice` deep link opens the same dialog; entering still needs one tap.
// There is no language control and no voice picker (U1).
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatProcessors } from './processor-disclosure';
import voiceCopy, { type VoiceCopyKey } from '@/i18n/ns/voice';
import { cn } from '@/lib/utils';
import { captureSupport } from '@/speech/capture/core';
import { micOwner } from '@/speech/capture/mic-owner';
import type { MicKind } from '@/speech/contracts';
import { useSpeech } from '@/speech/ui/speech-provider';
import { AudioLinesIcon } from 'lucide-react';
import { useEffect, useId, useState, useSyncExternalStore } from 'react';
import { useStore } from 'zustand';
import type { VoiceConfig } from './contracts';
import { voiceErrorCopy } from './error-copy';
import { saveVoicePrefs, voiceController, voiceStore } from './runtime';
import { useVoiceConfig } from './state';
import { barActive } from './store';

/** The `#/voice` deep link. */
export const VOICE_DEEP_LINK = '#/voice';

/** Why voice mode cannot start, as a copy key; null when it can. Exported for tests. */
export function voiceEntryBlocked(
  voice: VoiceConfig,
  micKind: MicKind | null,
  support: { mic: boolean; secure: boolean },
): VoiceCopyKey | null {
  if (!voice.available) {
    const copy = voiceErrorCopy('voice', 'voice_mode_unavailable', voice.reason);
    return copy?.ns === 'voice' ? copy.key : 'voiceUnavailable';
  }
  if (!voice.policy.voiceMode) return 'errorPolicyOff';
  if (!support.secure) return 'errorInsecure';
  if (!support.mic) return 'errorUnsupported';
  if (micKind === 'meeting' || micKind === 'enrolment') return 'voiceRefusedMeeting';
  return null;
}

const useMicKind = () => useSyncExternalStore(micOwner.subscribe, micOwner.current, () => null);

/** Start voice mode inside the gesture, and remember that the person turned it on. */
function start(voice: VoiceConfig) {
  voiceController().enter();
  if (!voice.prefs.enabled) void saveVoicePrefs({ enabled: true }).catch(() => {});
}

export function VoiceEntry() {
  const speech = useSpeech();
  const copy = useNamespaceCopy(voiceCopy);
  const locale = useAppCatalogLocale();
  const { voice, readAloud } = useVoiceConfig();
  const phase = useStore(voiceStore, (state) => state.phase);
  const micKind = useMicKind();
  const [disclosure, setDisclosure] = useState(false);
  const reasonId = useId();
  // Voice mode is useful only when spoken replies can complete the loop. The
  // output capability and company switch are the single source of truth; no
  // deployment style flag or personal configuration is needed to discover it.
  const offered = Boolean(
    speech?.availability.modes.voice && readAloud?.available && voice?.policy.voiceMode,
  );

  useEffect(() => {
    if (!offered || globalThis.location?.hash !== VOICE_DEEP_LINK) return;
    // Arms the entry once; the reload does not re-open it.
    globalThis.history?.replaceState(
      globalThis.history.state,
      '',
      `${location.pathname}${location.search}`,
    );
    setDisclosure(true);
  }, [offered]);

  if (!offered || !voice || barActive(phase)) return null;
  const reason = voiceEntryBlocked(voice, micKind, captureSupport());
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-slot="voice-entry"
        data-phase={phase}
        className={cn(
          'aui-composer-voice size-11 rounded-full',
          reason && 'text-muted-foreground/60 cursor-not-allowed hover:bg-transparent',
        )}
        aria-label={copy.enterVoice}
        aria-disabled={reason ? true : undefined}
        aria-describedby={reason ? reasonId : undefined}
        title={reason ? copy[reason] : copy.enterVoice}
        data-voice-blocked={reason ?? undefined}
        onClick={() => {
          if (reason) return;
          if (voice.prefs.enabled) start(voice);
          else setDisclosure(true);
        }}
      >
        <AudioLinesIcon className="size-5 md:size-4" aria-hidden="true" />
      </Button>
      {reason && (
        <span id={reasonId} className="sr-only">
          {copy[reason]}
        </span>
      )}
      <Dialog open={disclosure} onOpenChange={setDisclosure}>
        <DialogContent data-slot="voice-disclosure" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{copy.disclosureTitle}</DialogTitle>
            <DialogDescription>
              {formatCopy(copy.disclosureBody, {
                processors: formatProcessors(voice.processors, locale),
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="text-muted-foreground flex flex-col gap-2 text-sm">
            <p>{copy.disclosureApprovals}</p>
            <p>{copy.disclosureExit}</p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-slot="voice-not-now"
              className="min-h-11"
              onClick={() => setDisclosure(false)}
            >
              {copy.notNow}
            </Button>
            <Button
              type="button"
              data-slot="voice-turn-on"
              className="min-h-11"
              disabled={Boolean(reason)}
              onClick={() => {
                setDisclosure(false);
                start(voice);
              }}
            >
              {voice.prefs.enabled ? copy.enterVoice : copy.turnOn}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
